import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { BookShell } from "@/book/BookShell";
import { ChapterPage } from "@/book/pages/ChapterPage";
import { ErrataPage } from "@/book/pages/ErrataPage";
import { ExerciseDetailPage } from "@/book/pages/ExerciseDetailPage";
import { ExercisesPage } from "@/book/pages/ExercisesPage";
import { ExperiencesPage } from "@/book/pages/ExperiencesPage";
import { GlossaryPage } from "@/book/pages/GlossaryPage";
import { HomePage } from "@/book/pages/HomePage";
import { ProfilePage as BookProfilePage } from "@/book/pages/ProfilePage";
import { ReadingsPage } from "@/book/pages/ReadingsPage";
import { SearchPage } from "@/book/pages/SearchPage";
import { PublicRedirect } from "@/core/PublicRedirect";
import { LoadingScreen, RequireAuth, RequireMember } from "@/core/RequireAuth";
import { JoinPage } from "@/core/pages/JoinPage";
import { LoginPage } from "@/core/pages/LoginPage";
import { ProfilePage } from "@/core/pages/ProfilePage";
import { ProjectsPage } from "@/core/pages/ProjectsPage";
import { RegisterPage } from "@/core/pages/RegisterPage";
import { ViewFrame } from "@/core/ViewFrame";

// Az admin külön bundle: csak adminnak töltődik (rendszerterv 13.)
const AdminRoutes = lazy(() => import("./admin"));

export const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/app" replace /> },
  // A nyomtatott könyv QR-URL-je: rendes esetben az edge nginx irányít a `/konyv`-re; ez
  // csak tartalék (pl. nginx nélküli futtatás). A `/konyv` SSR-oldal, nem SPA-útvonal.
  { path: "/konyv-melleklet", element: <PublicRedirect to="/konyv?src=qr" /> },
  {
    // Mobilnézet nagy képernyőn (kivetítéshez): bekapcsolva ezek az oldalak telefon-keretben
    // futnak (`core/ViewFrame.tsx`); az admin kívül marad, mindig asztali.
    element: <ViewFrame />,
    children: [
      { path: "/app/belepes", element: <LoginPage /> },
      { path: "/app/regisztracio", element: <RegisterPage /> },
      {
        path: "/app",
        element: (
          <RequireAuth>
            <ProjectsPage />
          </RequireAuth>
        ),
      },
      {
        path: "/app/profil",
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      {
        path: "/app/book/csatlakozas",
        element: (
          <RequireAuth>
            <JoinPage project="book" />
          </RequireAuth>
        ),
      },
      {
        path: "/app/book",
        element: (
          <RequireMember project="book" joinPath="/app/book/csatlakozas">
            <BookShell />
          </RequireMember>
        ),
        children: [
          { index: true, element: <HomePage /> },
          { path: "profil", element: <BookProfilePage /> },
          // a fejezet-lista a kezdőlapon él — a régi cím oda visz
          { path: "fejezetek", element: <Navigate to="/app/book#fejezetek" replace /> },
          { path: "fejezetek/:chapterId", element: <ChapterPage /> },
          { path: "gyakorlatok", element: <ExercisesPage /> },
          { path: "gyakorlatok/:exerciseId", element: <ExerciseDetailPage /> },
          { path: "ajanlo", element: <ReadingsPage /> },
          // a régi cím (2026-09-12 előtt „Olvasnivalók") az Ajánlóra visz
          { path: "olvasnivalok", element: <Navigate to="/app/book/ajanlo" replace /> },
          { path: "tapasztalatok", element: <ExperiencesPage /> },
          { path: "fogalomtar", element: <GlossaryPage /> },
          { path: "hibajegyzek", element: <ErrataPage /> },
          { path: "kereses", element: <SearchPage /> },
          { path: "*", element: <Navigate to="/app/book" replace /> },
        ],
      },
    ],
  },
  {
    path: "/admin/*",
    element: (
      <RequireAuth admin>
        <Suspense fallback={<LoadingScreen />}>
          <AdminRoutes />
        </Suspense>
      </RequireAuth>
    ),
  },
  { path: "*", element: <Navigate to="/app" replace /> },
]);
